import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, NavLink } from "react-router-dom";
import {
  Home,
  AllPosts,
  SEOTools,
  TrafficTools,
  Autopost,
  Users,
  Subscribers,
  Campaigns,
  Queue,
  Reports,
  Templates,
  CampaignAnalytics,
} from "./pages";

// ✅ Import your SocialSettings component
import SocialSettings from "./pages/SocialSettings";

import { Menu, X, Bell, User, ChevronDown, ChevronRight } from "lucide-react";

export default function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [campaignsOpen, setCampaignsOpen] = useState(true);

  return (
    <Router>
      <div className="flex min-h-screen bg-gray-100">
        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-lg p-6 space-y-6 transform 
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} 
          transition-transform duration-300 lg:static lg:translate-x-0`}
        >
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-blue-600">Admin Panel</h1>
            <button
              className="lg:hidden text-gray-700"
              onClick={() => setSidebarOpen(false)}
            >
              <X size={24} />
            </button>
          </div>

          <nav className="space-y-4">
            <NavLink to="/" className="block" end>
              {({ isActive }) => (
                <span className={isActive ? "text-blue-600 font-semibold" : "text-gray-700 hover:text-blue-500"}>
                  Dashboard
                </span>
              )}
            </NavLink>

            <NavLink to="/posts" className="block">
              {({ isActive }) => (
                <span className={isActive ? "text-blue-600 font-semibold" : "text-gray-700 hover:text-blue-500"}>
                  All Posts
                </span>
              )}
            </NavLink>

            <NavLink to="/seo" className="block">
              {({ isActive }) => (
                <span className={isActive ? "text-blue-600 font-semibold" : "text-gray-700 hover:text-blue-500"}>
                  SEO Tools
                </span>
              )}
            </NavLink>

            <NavLink to="/traffic" className="block">
              {({ isActive }) => (
                <span className={isActive ? "text-blue-600 font-semibold" : "text-gray-700 hover:text-blue-500"}>
                  Traffic Tools
                </span>
              )}
            </NavLink>

            <NavLink to="/autopost" className="block">
              {({ isActive }) => (
                <span className={isActive ? "text-blue-600 font-semibold" : "text-gray-700 hover:text-blue-500"}>
                  Auto Post Social Media
                </span>
              )}
            </NavLink>

            {/* ✅ Fixed: separate SocialSettings link */}
            <NavLink to="/components/social-settings" className="block">
              {({ isActive }) => (
                <span className={isActive ? "text-blue-600 font-semibold" : "text-gray-700 hover:text-blue-500"}>
                  Social Settings
                </span>
              )}
            </NavLink>

            <NavLink to="/users" className="block">
              {({ isActive }) => (
                <span className={isActive ? "text-blue-600 font-semibold" : "text-gray-700 hover:text-blue-500"}>
                  Users
                </span>
              )}
            </NavLink>

            {/* Campaigns Menu */}
            <div>
              <button
                onClick={() => setCampaignsOpen(!campaignsOpen)}
                className="flex items-center justify-between w-full text-gray-700 hover:text-blue-500 font-semibold"
              >
                Campaigns
                {campaignsOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              {campaignsOpen && (
                <div className="ml-4 mt-2 space-y-1">
                  <NavLink to="/campaigns" className="block">
                    {({ isActive }) => (
                      <span className={isActive ? "text-blue-600 font-semibold" : "text-gray-700 hover:text-blue-500"}>
                        All Campaigns
                      </span>
                    )}
                  </NavLink>
                  <NavLink to="/queue" className="block">
                    {({ isActive }) => (
                      <span className={isActive ? "text-blue-600 font-semibold" : "text-gray-700 hover:text-blue-500"}>
                        Queue
                      </span>
                    )}
                  </NavLink>
                  <NavLink to="/subscribers" className="block">
                    {({ isActive }) => (
                      <span className={isActive ? "text-blue-600 font-semibold" : "text-gray-700 hover:text-blue-500"}>
                        Subscribers
                      </span>
                    )}
                  </NavLink>
                  <NavLink to="/reports" className="block">
                    {({ isActive }) => (
                      <span className={isActive ? "text-blue-600 font-semibold" : "text-gray-700 hover:text-blue-500"}>
                        Reports
                      </span>
                    )}
                  </NavLink>
                  <NavLink to="/templates" className="block">
                    {({ isActive }) => (
                      <span className={isActive ? "text-blue-600 font-semibold" : "text-gray-700 hover:text-blue-500"}>
                        Templates
                      </span>
                    )}
                  </NavLink>
                  <NavLink to="/campaign-analytics" className="block">
                    {({ isActive }) => (
                      <span className={isActive ? "text-blue-600 font-semibold" : "text-gray-700 hover:text-blue-500"}>
                        Analytics
                      </span>
                    )}
                  </NavLink>
                </div>
              )}
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Topbar */}
          <header className="flex justify-between items-center bg-white p-4 shadow-md sticky top-0 z-30">
            <div className="flex items-center space-x-4">
              <button
                className="lg:hidden text-gray-700"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu size={24} />
              </button>
              <h2 className="text-xl font-bold text-gray-800">
                Welcome Back 👋
              </h2>
            </div>

            <div className="flex items-center space-x-4">
              <button className="relative text-gray-600 hover:text-blue-600">
                <Bell size={22} />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1">
                  3
                </span>
              </button>
              <div className="flex items-center space-x-2">
                <User size={24} className="text-gray-600" />
                <span className="font-semibold">Admin</span>
              </div>
              <button className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow hover:bg-blue-700">
                Logout
              </button>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 p-6">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/posts" element={<AllPosts />} />
              <Route path="/seo" element={<SEOTools />} />
              <Route path="/traffic" element={<TrafficTools />} />
              <Route path="/autopost" element={<Autopost apiUrl={`${process.env.REACT_APP_API}post.php`} />} />
              {/* ✅ Fixed: SocialSettings route */}
              <Route path="/components/social-settings" element={<SocialSettings />} />
              <Route path="/users" element={<Users />} />

              {/* Campaign Routes */}
              <Route path="/campaigns" element={<Campaigns />} />
              <Route path="/queue" element={<Queue />} />
              <Route path="/subscribers" element={<Subscribers />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/templates" element={<Templates />} />
              <Route
                path="/campaign-analytics"
                element={<CampaignAnalytics campaignId={1} />}
              />
            </Routes>
          </main>
        </div>
      </div>
    </Router>
  );
}
